import { useState } from "react";
import { useNavigate } from "react-router-dom";
import "@/App.css";
import { Menu, X, Mail, Phone, Instagram, MessageCircle, ArrowRight, Users, Briefcase, Target, Zap, Shield, TrendingUp, LogIn, Settings } from "lucide-react";
import axios from "axios";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

function HomePage() {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    service: "general"
  });
  const [formStatus, setFormStatus] = useState("");

  const contactInfo = {
    whatsapp: "+5535991743960",
    email: "alex.philippe@arcanoprojetos.com",
    instagram: "arcano.pcs"
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setIsMenuOpen(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus("sending");
    
    try {
      await axios.post(`${API}/contact`, formData);
      setFormStatus("success");
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
        service: "general"
      });
      setTimeout(() => setFormStatus(""), 5000);
    } catch (error) {
      console.error("Error sending message:", error);
      setFormStatus("error");
      setTimeout(() => setFormStatus(""), 5000);
    }
  };

  const openWhatsApp = () => {
    window.open(`https://wa.me/${contactInfo.whatsapp.replace(/\D/g, '')}?text=Olá! Gostaria de saber mais sobre os serviços da Arcano Projetos.`, "_blank");
  };

  return (
    <div className="App">
      {/* Header/Navbar */}
      <nav className="fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-50 shadow-sm" data-testid="main-navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex-shrink-0" data-testid="logo-container">
              <h1 className="text-2xl font-bold tracking-tight text-arcano-blue">ARCANO</h1>
              <p className="text-xs text-gray-500 -mt-1">Projetos de Tecnologia</p>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-arcano-blue transition-colors" data-testid="nav-home">Home</button>
              <button onClick={() => scrollToSection('produtos')} className="text-gray-700 hover:text-arcano-blue transition-colors" data-testid="nav-produtos">Produtos</button>
              <button onClick={() => scrollToSection('sobre')} className="text-gray-700 hover:text-arcano-blue transition-colors" data-testid="nav-sobre">Sobre</button>
              <button onClick={() => scrollToSection('contato')} className="text-gray-700 hover:text-arcano-blue transition-colors" data-testid="nav-contato">Contato</button>
              <button onClick={() => navigate('/login')} className="text-gray-700 hover:text-arcano-blue transition-colors flex items-center gap-2" data-testid="nav-area-cliente">
                <LogIn size={18} />
                Área do Cliente
              </button>
              <button onClick={openWhatsApp} className="bg-arcano-blue text-white px-6 py-2 rounded-md hover:bg-arcano-blue-dark transition-colors flex items-center gap-2" data-testid="nav-whatsapp-button">
                <MessageCircle size={18} />
                Fale Conosco
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 text-arcano-blue"
              data-testid="mobile-menu-toggle"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200" data-testid="mobile-menu">
            <div className="px-4 py-4 space-y-3">
              <button onClick={() => scrollToSection('home')} className="block w-full text-left py-2 text-gray-700 hover:text-arcano-blue">Home</button>
              <button onClick={() => scrollToSection('produtos')} className="block w-full text-left py-2 text-gray-700 hover:text-arcano-blue">Produtos</button>
              <button onClick={() => scrollToSection('sobre')} className="block w-full text-left py-2 text-gray-700 hover:text-arcano-blue">Sobre</button>
              <button onClick={() => scrollToSection('contato')} className="block w-full text-left py-2 text-gray-700 hover:text-arcano-blue">Contato</button>
              <button onClick={() => navigate('/login')} className="w-full border-2 border-arcano-blue text-arcano-blue px-6 py-2 rounded-md hover:bg-arcano-blue hover:text-white transition-colors flex items-center justify-center gap-2">
                <LogIn size={18} />
                Área do Cliente
              </button>
              <button onClick={openWhatsApp} className="w-full bg-arcano-blue text-white px-6 py-2 rounded-md hover:bg-arcano-blue-dark transition-colors flex items-center justify-center gap-2">
                <MessageCircle size={18} />
                Fale Conosco
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="pt-24 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-arcano-blue-light" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6 text-gray-900" data-testid="hero-title">
              Transformamos<br />Tecnologia em<br />Resultados
            </h1>
            <p className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto" data-testid="hero-subtitle">
              Soluções estratégicas em tecnologia para empresas que buscam inovação, crescimento e excelência operacional
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => scrollToSection('produtos')}
                className="bg-arcano-blue text-white px-8 py-4 rounded-md hover:bg-arcano-blue-dark transition-colors flex items-center justify-center gap-2 text-lg font-semibold"
                data-testid="hero-cta-produtos"
              >
                Conheça Nossos Produtos
                <ArrowRight size={20} />
              </button>
              <button
                onClick={openWhatsApp}
                className="border-2 border-arcano-blue text-arcano-blue px-8 py-4 rounded-md hover:bg-arcano-blue hover:text-white transition-colors flex items-center justify-center gap-2 text-lg font-semibold"
                data-testid="hero-cta-whatsapp"
              >
                <MessageCircle size={20} />
                Fale com Especialista
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Produtos Section */}
      <section id="produtos" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="produtos-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900" data-testid="produtos-title">Nossos Produtos</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Duas soluções estratégicas para impulsionar seu negócio
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Rent a Squad */}
            <div className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden hover:border-arcano-blue hover:shadow-xl transition-all" data-testid="produto-rent-a-squad">
              <div className="h-64 overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/7988079/pexels-photo-7988079.jpeg?auto=compress&cs=tinysrgb&w=800"
                  alt="Rent a Squad - Equipe de tecnologia"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-arcano-blue-light rounded-lg">
                    <Users size={32} className="text-arcano-blue" />
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900">Rent a Squad</h3>
                </div>
                <p className="text-gray-600 mb-6 text-lg leading-relaxed">
                  Hunting e alocação de profissionais de tecnologia de alto nível. Montamos o time perfeito para seu projeto, com desenvolvedores, designers, arquitetos de software e gestores especializados.
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-arcano-blue rounded-full mt-2"></div>
                    <span className="text-gray-700">Profissionais selecionados e validados</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-arcano-blue rounded-full mt-2"></div>
                    <span className="text-gray-700">Alocação flexível por projeto ou período</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-arcano-blue rounded-full mt-2"></div>
                    <span className="text-gray-700">Especialistas em diversas tecnologias</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-arcano-blue rounded-full mt-2"></div>
                    <span className="text-gray-700">Gestão completa de equipes remotas</span>
                  </li>
                </ul>
                <button
                  onClick={openWhatsApp}
                  className="w-full bg-arcano-blue text-white py-3 rounded-md hover:bg-arcano-blue-dark transition-colors font-semibold"
                  data-testid="rent-a-squad-cta"
                >
                  Solicitar Orçamento
                </button>
              </div>
            </div>

            {/* Consultoria */}
            <div className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden hover:border-arcano-blue hover:shadow-xl transition-all" data-testid="produto-advisors">
              <div className="h-64 overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/2977565/pexels-photo-2977565.jpeg?auto=compress&cs=tinysrgb&w=800"
                  alt="Consultoria - Consultoria tecnológica"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-arcano-blue-light rounded-lg">
                    <Briefcase size={32} className="text-arcano-blue" />
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900">Consultoria</h3>
                </div>
                <p className="text-gray-600 mb-6 text-lg leading-relaxed">
                  Consultoria estratégica de estruturação de TI para empresas e startups. Transformamos sua visão em realidade através de tecnologia, processos e inovação.
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-arcano-blue rounded-full mt-2"></div>
                    <span className="text-gray-700">Planejamento estratégico de TI</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-arcano-blue rounded-full mt-2"></div>
                    <span className="text-gray-700">Arquitetura de sistemas e infraestrutura</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-arcano-blue rounded-full mt-2"></div>
                    <span className="text-gray-700">Processos de desenvolvimento ágil</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-arcano-blue rounded-full mt-2"></div>
                    <span className="text-gray-700">Inovação e transformação digital</span>
                  </li>
                </ul>
                <button
                  onClick={openWhatsApp}
                  className="w-full bg-arcano-blue text-white py-3 rounded-md hover:bg-arcano-blue-dark transition-colors font-semibold"
                  data-testid="advisors-cta"
                >
                  Agendar Consultoria
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Diferenciais Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="diferenciais-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">Por Que Arcano?</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Diferenciais que nos tornam referência em tecnologia
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6 rounded-lg hover:bg-arcano-blue-light transition-colors" data-testid="diferencial-excelencia">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-arcano-blue-light border-2 border-arcano-blue rounded-full mb-4">
                <Target size={40} className="text-arcano-blue" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Excelência Técnica</h3>
              <p className="text-gray-600">
                Profissionais com expertise comprovada nas mais modernas tecnologias do mercado
              </p>
            </div>

            <div className="text-center p-6 rounded-lg hover:bg-arcano-blue-light transition-colors" data-testid="diferencial-agilidade">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-arcano-blue-light border-2 border-arcano-blue rounded-full mb-4">
                <Zap size={40} className="text-arcano-blue" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Agilidade e Flexibilidade</h3>
              <p className="text-gray-600">
                Adaptamos nossas soluções às necessidades específicas de cada cliente
              </p>
            </div>

            <div className="text-center p-6 rounded-lg hover:bg-arcano-blue-light transition-colors" data-testid="diferencial-seguranca">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-arcano-blue-light border-2 border-arcano-blue rounded-full mb-4">
                <Shield size={40} className="text-arcano-blue" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Confiança e Segurança</h3>
              <p className="text-gray-600">
                Processos robustos e transparentes em todas as etapas do projeto
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sobre Section */}
      <section id="sobre" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="sobre-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900" data-testid="sobre-title">Sobre a Arcano</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                A tecnologia se tornou um pilar essencial para o dia a dia de muitas empresas, o que culmina na necessidade de novas soluções, ferramentas e, claro, no desenvolvimento de sistemas de alto desempenho para atingir objetivos estratégicos e se manter competitivo no mercado.
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                A Arcano Projetos de Tecnologia atua como parceira estratégica de empresas que buscam excelência tecnológica. Nossa missão é transformar desafios complexos em soluções inovadoras que impulsionam resultados reais e mensuráveis.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                <strong>Nossas especialidades:</strong> Hunting de talentos qualificados, estruturação de equipes de alta performance e consultoria especializada em processos, tecnologia e inovação. Conectamos pessoas e tecnologia para construir o futuro do seu negócio.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-8">
                <div className="bg-white p-5 rounded-lg border-l-4 border-arcano-blue shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="text-arcano-blue flex-shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                        <circle cx="9" cy="7" r="4"></circle>
                        <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                      </svg>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-arcano-blue">30+</p>
                      <p className="text-sm text-gray-600">Clientes atendidos</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-5 rounded-lg border-l-4 border-arcano-blue shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="text-arcano-blue flex-shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                        <circle cx="9" cy="7" r="4"></circle>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                      </svg>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-arcano-blue">+100</p>
                      <p className="text-sm text-gray-600">Talentos contratados</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-5 rounded-lg border-l-4 border-arcano-blue shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="text-arcano-blue flex-shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-arcano-blue">+3.8M</p>
                      <p className="text-sm text-gray-600">Valor em consultoria</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg overflow-hidden shadow-lg border-4 border-arcano-blue">
              <img
                src="https://images.pexels.com/photos/6804068/pexels-photo-6804068.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Equipe Arcano Projetos"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Clientes Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="clientes-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">Nossos Clientes</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Empresas que confiam na Arcano para transformar tecnologia em resultados
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Helptech Informática */}
            <div className="bg-white border-2 border-gray-200 rounded-lg p-8 hover:border-arcano-blue hover:shadow-xl transition-all text-center" data-testid="cliente-helptech">
              <div className="w-32 h-32 mx-auto mb-4 flex items-center justify-center">
                <img 
                  src="https://helptechpc.com.br/wp-content/uploads/2024/04/logo-azul-mini-02.png" 
                  alt="Helptech Informática"
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Helptech Informática</h3>
              <p className="text-gray-600">Soluções em TI e Tecnologia</p>
            </div>

            {/* BNP Soluções */}
            <div className="bg-white border-2 border-gray-200 rounded-lg p-8 hover:border-arcano-blue hover:shadow-xl transition-all text-center" data-testid="cliente-bnp">
              <div className="w-32 h-32 mx-auto mb-4 flex items-center justify-center">
                <img 
                  src="https://bnpsolucoes.com.br/wp-content/uploads/2023/08/logo_black.png" 
                  alt="BNP Soluções"
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">BNP Soluções</h3>
              <p className="text-gray-600">Inteligência Artificial e Nuvem</p>
            </div>

            {/* Fazenda Mariano */}
            <div className="bg-white border-2 border-gray-200 rounded-lg p-8 hover:border-arcano-blue hover:shadow-xl transition-all text-center" data-testid="cliente-fazenda-mariano">
              <div className="w-32 h-32 mx-auto mb-4 flex items-center justify-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_61b748f5-4cc4-47b0-9f16-c1b94d0ddaf0/artifacts/ukov728h_logofmarianosimbolo300x271.jpg" 
                  alt="Fazenda Mariano"
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Fazenda Mariano</h3>
              <p className="text-gray-600">Produção de Café Especial</p>
            </div>

            {/* VRX */}
            <div className="bg-white border-2 border-gray-200 rounded-lg p-8 hover:border-arcano-blue hover:shadow-xl transition-all text-center" data-testid="cliente-vrx">
              <div className="w-32 h-32 mx-auto mb-4 flex items-center justify-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_61b748f5-4cc4-47b0-9f16-c1b94d0ddaf0/artifacts/b1sqyxka_vrx%20logo.jpeg" 
                  alt="VRX"
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">VRX</h3>
              <p className="text-gray-600">Experiência em Realidade Virtual</p>
            </div>

            {/* AxxisPay */}
            <div className="bg-white border-2 border-gray-200 rounded-lg p-8 hover:border-arcano-blue hover:shadow-xl transition-all text-center" data-testid="cliente-axxispay">
              <div className="w-32 h-32 mx-auto mb-4 flex items-center justify-center">
                <img 
                  src="https://axxispay.com.br/images/Logo.png" 
                  alt="AxxisPay"
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">AxxisPay</h3>
              <p className="text-gray-600">Pagamentos Digitais</p>
            </div>

            {/* Portal de Compras Públicas */}
            <div className="bg-white border-2 border-gray-200 rounded-lg p-8 hover:border-arcano-blue hover:shadow-xl transition-all text-center" data-testid="cliente-portal-compras">
              <div className="w-32 h-32 mx-auto mb-4 flex items-center justify-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_61b748f5-4cc4-47b0-9f16-c1b94d0ddaf0/artifacts/s60lg5yg_portal%20de%20compras%20publicas.png" 
                  alt="Portal de Compras Públicas"
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Portal de Compras Públicas</h3>
              <p className="text-gray-600">Licitações e Compras Governamentais</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contato Section */}
      <section id="contato" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="contato-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900" data-testid="contato-title">Entre em Contato</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Vamos conversar sobre como podemos ajudar seu negócio
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium mb-2 text-gray-700">Nome</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
                    data-testid="contact-form-name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium mb-2 text-gray-700">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
                    data-testid="contact-form-email"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium mb-2 text-gray-700">Telefone</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
                    data-testid="contact-form-phone"
                  />
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm font-medium mb-2 text-gray-700">Serviço de Interesse</label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
                    data-testid="contact-form-service"
                  >
                    <option value="general">Geral</option>
                    <option value="rent-a-squad">Rent a Squad</option>
                    <option value="advisors">Consultoria</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium mb-2 text-gray-700">Assunto</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
                    data-testid="contact-form-subject"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium mb-2 text-gray-700">Mensagem</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
                    data-testid="contact-form-message"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={formStatus === "sending"}
                  className="w-full bg-arcano-blue text-white py-4 rounded-md hover:bg-arcano-blue-dark transition-colors disabled:opacity-50 font-semibold"
                  data-testid="contact-form-submit"
                >
                  {formStatus === "sending" ? "Enviando..." : "Enviar Mensagem"}
                </button>

                {formStatus === "success" && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md" data-testid="contact-form-success">
                    Mensagem enviada com sucesso! Entraremos em contato em breve.
                  </div>
                )}

                {formStatus === "error" && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md" data-testid="contact-form-error">
                    Erro ao enviar mensagem. Tente novamente ou entre em contato via WhatsApp.
                  </div>
                )}
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold mb-6 text-gray-900">Informações de Contato</h3>
                <div className="space-y-6">
                  <a
                    href={`https://wa.me/${contactInfo.whatsapp.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-4 p-4 border-2 border-gray-200 rounded-lg hover:border-arcano-blue hover:shadow-lg transition-all"
                    data-testid="contact-info-whatsapp"
                  >
                    <MessageCircle size={24} className="flex-shrink-0 mt-1 text-arcano-blue" />
                    <div>
                      <p className="font-medium text-gray-900">WhatsApp</p>
                      <p className="text-gray-600">{contactInfo.whatsapp}</p>
                    </div>
                  </a>

                  <a
                    href={`mailto:${contactInfo.email}`}
                    className="flex items-start gap-4 p-4 border-2 border-gray-200 rounded-lg hover:border-arcano-blue hover:shadow-lg transition-all"
                    data-testid="contact-info-email"
                  >
                    <Mail size={24} className="flex-shrink-0 mt-1 text-arcano-blue" />
                    <div>
                      <p className="font-medium text-gray-900">Email</p>
                      <p className="text-gray-600">{contactInfo.email}</p>
                    </div>
                  </a>

                  <a
                    href={`https://instagram.com/${contactInfo.instagram}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-4 p-4 border-2 border-gray-200 rounded-lg hover:border-arcano-blue hover:shadow-lg transition-all"
                    data-testid="contact-info-instagram"
                  >
                    <Instagram size={24} className="flex-shrink-0 mt-1 text-arcano-blue" />
                    <div>
                      <p className="font-medium text-gray-900">Instagram</p>
                      <p className="text-gray-600">@{contactInfo.instagram}</p>
                    </div>
                  </a>
                </div>
              </div>

              <div className="bg-arcano-blue-light p-8 rounded-lg border-2 border-arcano-blue">
                <h4 className="text-xl font-bold mb-4 text-gray-900">Horário de Atendimento</h4>
                <p className="text-gray-700 mb-2">Segunda a Sexta: 9h às 18h</p>
                <p className="text-gray-700">Sábados: 9h às 13h</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-arcano-blue-dark text-white py-12 px-4 sm:px-6 lg:px-8" data-testid="footer">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-2">ARCANO</h3>
              <p className="text-blue-200 text-sm mb-4">Projetos de Tecnologia</p>
              <p className="text-blue-100">
                Transformando tecnologia em resultados para empresas inovadoras
              </p>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Navegação</h4>
              <ul className="space-y-2 text-blue-100">
                <li><button onClick={() => scrollToSection('home')} className="hover:text-white transition-colors">Home</button></li>
                <li><button onClick={() => scrollToSection('produtos')} className="hover:text-white transition-colors">Produtos</button></li>
                <li><button onClick={() => scrollToSection('sobre')} className="hover:text-white transition-colors">Sobre</button></li>
                <li><button onClick={() => scrollToSection('contato')} className="hover:text-white transition-colors">Contato</button></li>
                <li><button onClick={() => navigate('/login')} className="hover:text-white transition-colors">Área do Cliente</button></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Produtos</h4>
              <ul className="space-y-2 text-blue-100">
                <li><span className="hover:text-white transition-colors">Rent a Squad</span></li>
                <li><span className="hover:text-white transition-colors">Consultoria</span></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-blue-700 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-blue-100 text-sm mb-4 md:mb-0">
              © 2025 Arcano Projetos de Tecnologia. Todos os direitos reservados.
            </p>
            <div className="flex items-center gap-6">
              <button
                onClick={() => navigate('/admin/login')}
                className="text-blue-300 hover:text-white transition-colors flex items-center gap-1 text-sm"
                data-testid="footer-admin"
              >
                <Settings size={16} />
                Admin
              </button>
              <a
                href={`https://wa.me/${contactInfo.whatsapp.replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-100 hover:text-white transition-colors"
                data-testid="footer-whatsapp"
              >
                <MessageCircle size={24} />
              </a>
              <a
                href={`mailto:${contactInfo.email}`}
                className="text-blue-100 hover:text-white transition-colors"
                data-testid="footer-email"
              >
                <Mail size={24} />
              </a>
              <a
                href={`https://instagram.com/${contactInfo.instagram}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-100 hover:text-white transition-colors"
                data-testid="footer-instagram"
              >
                <Instagram size={24} />
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <button
        onClick={openWhatsApp}
        className="fixed bottom-6 right-6 bg-green-500 text-white p-4 rounded-full shadow-lg hover:bg-green-600 transition-colors z-40"
        data-testid="floating-whatsapp-button"
      >
        <MessageCircle size={28} />
      </button>
    </div>
  );
}

export default HomePage;